import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Box, LinkText, TextArea, Button, ShadowBox } from '@components/common';
import StarRating from '@components/StarRating';
import { TableHeading } from '@components/Typography';
import TableAccessaryLabel from '@components/TableAccessaryLabel';
import TableCell from '@components/TableCell';
import CleansActions, { CleansSelectors } from '@redux/CleansRedux';
import { AuthSelectors } from '@redux/AuthRedux';
import API from '@utils/api';
import { stripePriceConverter, transformClean } from '@services/transform';
import RescheduleModal from './RescheduleModal';
import RebookingConfirmModal from './RebookingConfirmModal';
import { trackReview } from '@services/analytics';
import history from '@utils/history';

const CleanButton = styled(TableAccessaryLabel)`
  width: 102px;
  max-width: 102px;
  pointer-events: none;
`;

function CleanRate(props) {
  const {
    currentClean,
    appData,
    updateCurrentClean,
    isPMUser,
    initialRate,
    isRecurringClean,
    properties
  } = props;
  const [rating, setRating] = useState(initialRate || 0);
  const [isEditing, setEditing] = useState(true);
  const [ratingComment, setComment] = useState('');
  const [showReschedule, toggleReschedule] = useState(false);
  const [showRebookConfrim, toggleRebookConfirm] = useState(false);

  const {
    rating: cleanRating,
    ratingComment: cleanRatingComment,
    status
  } = currentClean;

  useEffect(() => {
    setRating(cleanRating || initialRate);
    setComment(cleanRatingComment || '');
  }, [cleanRating, cleanRatingComment]);

  const updateRate = async () => {
    const { bookingID: bookingId } = currentClean;

    const { result } = await API.setCleanRate(bookingId, {
      rating,
      ratingComment: ratingComment ? ratingComment : undefined
    });
    trackReview(result);

    if (!isRecurringClean) {
      toggleRebookConfirm(true);
    }

    updateCurrentClean(
      transformClean(result, appData.serviceTypes, appData.addons, properties)
    );
  };

  const renderRating = () => {
    const readOnly = !!cleanRating || isPMUser || status !== 'cleaned';
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        mb={4}
        py={1}
      >
        <StarRating
          readOnly={readOnly}
          isEditing={isEditing}
          value={rating}
          onChange={val => {
            setRating(val);
            setEditing(false);
          }}
        />
        <TextArea
          value={ratingComment}
          readOnly={readOnly}
          mt={2}
          height={160}
          onChange={e => {
            setComment(e.target.value);
          }}
          placeholder="The place looks great!"
        />
        {!readOnly && (
          <Button width={1} variant="primary" onClick={updateRate}>
            Submit
          </Button>
        )}
      </Box>
    );
  };

  const renderCleanInfo = () => {
    const {
      formattedDate,
      additionalService,
      serviceType,
      timeframe,
      timeframeTitle
    } = currentClean;
    const { title, icon } = serviceType;
    const addonIcons =
      additionalService && additionalService.length > 0
        ? additionalService.map(({ icon }) => icon)
        : undefined;
    const addonIconTooltips =
      additionalService && additionalService.length > 0
        ? additionalService.reduce(
            (acc, { icon, title }) => ({ ...acc, [icon]: title }),
            {}
          )
        : undefined;
    const addonText = addonIcons ? undefined : 'None';

    return (
      <ShadowBox mb={4}>
        <TableCell
          title="Type"
          renderAccessories={() => (
            <CleanButton
              p={1}
              flexDirection="row"
              justifyContent="space-between"
              title={title}
              icon={icon}
              tooltip={title}
              size="small"
              onClick={() => {}}
            />
          )}
        />
        <TableCell
          title="Additional services"
          rightIcons={addonIcons}
          iconTooltips={addonIconTooltips}
          rightIconColor="gold"
          rightText={addonText}
        />
        <TableCell title="Service date" rightText={formattedDate} />
        <TableCell
          title="Service time"
          rightIcons={[timeframe]}
          rightText={timeframeTitle}
          rightIconColor="gold"
        />
      </ShadowBox>
    );
  };

  const renderReceipt = () => {
    const {
      cost: receitDetail,
      formattedDate,
      additionalService,
      serviceType
    } = currentClean;
    const { title } = serviceType;
    const { cost: total, addonsPrice, baseServiceCost } = receitDetail || {};

    return (
      <ShadowBox mb={4}>
        <TableCell title="Payment date" rightText={formattedDate} />
        {baseServiceCost && (
          <TableCell
            p={3}
            title={title}
            rightText={`$${stripePriceConverter(baseServiceCost)}`}
          />
        )}
        {addonsPrice &&
          addonsPrice.length > 0 &&
          addonsPrice.map(({ addon, addonCost }) => {
            const addonService = additionalService.find(
              ({ name }) => addon === name
            );
            const { title } = addonService || {};
            return (
              <TableCell
                p={3}
                key={addon}
                title={title}
                rightText={`$${stripePriceConverter(addonCost)}`}
              />
            );
          })}

        <TableCell
          title="Total"
          rightText={`$${stripePriceConverter(total)}`}
        />
        <LinkText p={2} textAlign="center" disabled>
          Download receipt
        </LinkText>
      </ShadowBox>
    );
  };

  return (
    <React.Fragment>
      <TableHeading mt={[4, 0]} px={2} mb={4}>
        Service summary
      </TableHeading>
      {renderCleanInfo()}
      <TableHeading mt={0} px={2} mb={4}>
        What do you think?
      </TableHeading>
      {renderRating()}
      <TableHeading mt={0} px={2} mb={4}>
        Receipt
      </TableHeading>
      {renderReceipt()}
      <RescheduleModal
        show={showReschedule}
        dismiss={() => toggleReschedule(false)}
      />
      <RebookingConfirmModal
        show={showRebookConfrim}
        dismiss={() => toggleRebookConfirm(false)}
        isRebooking={rating > 4}
        onSubmit={() => {
          toggleReschedule(true);
        }}
        onFail={() => {
          history.push('/');
        }}
      />
    </React.Fragment>
  );
}

CleanRate.propTypes = {
  currentClean: PropTypes.object,
  appData: PropTypes.object,
  updateCurrentClean: PropTypes.func,
  isPMUser: PropTypes.bool,
  initialRate: PropTypes.number,
  isRecurringClean: PropTypes.bool,
  properties: PropTypes.array
};

const mapStatesToProps = state => ({
  isPMUser: AuthSelectors.selectIsPMUser(state),
  currentClean: CleansSelectors.selectCurrentClean(state),
  isRecurringClean: CleansSelectors.isRecurringClean(state),
  appData: state.app.data,
  properties: AuthSelectors.selectUserProperties(state)
});
const mapDispatchToProps = dispatch => ({
  updateCurrentClean: data => dispatch(CleansActions.setCurrentClean(data))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(CleanRate);
